import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { sendRejectedReportSMS } from './smsService';

const COLLECTION_NAME = 'vehicleChecks';

const getSortDate = (check) => {
  if (check.createdAt?.toDate) return check.createdAt.toDate().getTime();
  if (check.submittedAt?.toDate) return check.submittedAt.toDate().getTime();
  if (check.reviewedAt?.toDate) return check.reviewedAt.toDate().getTime();
  if (check.checkDate) return new Date(check.checkDate).getTime();
  return 0;
};

// Submit a daily vehicle check
export const submitVehicleCheck = async (checkData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...checkData,
      status: 'pending', // pending, approved, rejected
      createdAt: serverTimestamp(),
      submittedAt: serverTimestamp()
    });

    return {
      id: docRef.id,
      success: true
    };
  } catch (error) {
    console.error('Error submitting vehicle check:', error);
    throw error;
  }
};

// Get all vehicle checks (for admin)
export const getAllVehicleChecks = async (filterStatus = null) => {
  try {
    let q;

    if (filterStatus && filterStatus !== 'all') {
      q = query(
        collection(db, COLLECTION_NAME),
        where('status', '==', filterStatus)
      );
    } else {
      q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const checks = [];

    querySnapshot.forEach((doc) => {
      checks.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return checks.sort((a, b) => getSortDate(b) - getSortDate(a));
  } catch (error) {
    console.error('Error getting vehicle checks:', error);
    throw error;
  }
};

// Get vehicle checks by driver
export const getVehicleChecksByDriver = async (driverId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('driverId', '==', driverId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const checks = [];

    querySnapshot.forEach((doc) => {
      checks.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return checks;
  } catch (error) {
    console.error('Error getting driver vehicle checks:', error);
    throw error;
  }
};

// Get vehicle checks by vehicle
export const getVehicleChecksByVehicle = async (vehicleId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('vehicleId', '==', vehicleId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const checks = [];

    querySnapshot.forEach((doc) => {
      checks.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return checks;
  } catch (error) {
    console.error('Error getting vehicle checks:', error);
    throw error;
  }
};

// Get today's check for a specific driver
export const getTodayCheckForDriver = async (driverId) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('driverId', '==', driverId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    // Check if there's a check from today
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const checkDate = data.createdAt?.toDate();

      if (checkDate) {
        checkDate.setHours(0, 0, 0, 0);
        if (checkDate.getTime() === today.getTime()) {
          return {
            id: doc.id,
            ...data
          };
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting today check:', error);
    throw error;
  }
};

// Get today's check for a specific driver + vehicle combination
export const getTodayCheckForDriverAndVehicle = async (driverId, vehicleId) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('driverId', '==', driverId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      if (data.vehicleId !== vehicleId) continue;

      const checkDate = data.createdAt?.toDate();
      if (checkDate) {
        checkDate.setHours(0, 0, 0, 0);
        if (checkDate.getTime() === today.getTime()) {
          return { id: docSnap.id, ...data };
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting today check for vehicle:', error);
    throw error;
  }
};

// Approve a vehicle check
export const approveVehicleCheck = async (checkId, reviewNote = '') => {
  try {
    const checkRef = doc(db, COLLECTION_NAME, checkId);
    await updateDoc(checkRef, {
      status: 'approved',
      reviewNote,
      reviewedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error approving vehicle check:', error);
    throw error;
  }
};

// Update an existing vehicle check for resubmission
export const updateVehicleCheck = async (checkId, checkData) => {
  try {
    const checkRef = doc(db, COLLECTION_NAME, checkId);
    await updateDoc(checkRef, {
      ...checkData,
      status: 'pending',
      reviewNote: '',
      reviewedAt: null,
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      resubmittedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating vehicle check:', error);
    throw error;
  }
};

// Reject a vehicle check
export const rejectVehicleCheck = async (checkId, reviewNote, checkData = null) => {
  try {
    const checkRef = doc(db, COLLECTION_NAME, checkId);
    await updateDoc(checkRef, {
      status: 'rejected',
      reviewNote,
      reviewedAt: serverTimestamp()
    });

    let checkDetails = checkData;

    if (!checkDetails) {
      const checkSnapshot = await getDoc(checkRef);
      if (checkSnapshot.exists()) {
        checkDetails = checkSnapshot.data();
      }
    }

    if (checkDetails?.driverPhone) {
      try {
        await sendRejectedReportSMS(
          {
            name: checkDetails.driverName,
            phone: checkDetails.driverPhone
          },
          reviewNote
        );
      } catch (smsError) {
        console.error('Error sending rejection SMS:', smsError);
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error rejecting vehicle check:', error);
    throw error;
  }
};
